import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  AdminZoneOption,
  createAdminZoneOption,
  getAdminZoneOptions,
} from '../../services/admin';

type ZoneOption = AdminZoneOption & {
  inputValue?: string;
  isCreateOption?: boolean;
};

const filter = createFilterOptions<ZoneOption>();

interface AdminZoneAutocompleteProps {
  value: AdminZoneOption | null;
  onChange: (zone: AdminZoneOption | null) => void;
  showSnackbar: (msg: string, type: 'success' | 'error') => void;
  label?: string;
}

const AdminZoneAutocomplete: React.FC<AdminZoneAutocompleteProps> = ({
  value,
  onChange,
  showSnackbar,
  label = 'Location',
}) => {
  const [options, setOptions] = useState<AdminZoneOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const selectedValue = useMemo<ZoneOption | null>(() => {
    if (!value) return null;
    return options.find((option) => option._id === value._id) || value;
  }, [options, value]);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await getAdminZoneOptions();
      setOptions(response.data || []);
    } catch {
      showSnackbar('Failed to load locations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);

  const handleCreateZone = async (name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      setCreating(true);
      const response = await createAdminZoneOption(trimmedName);
      const created = response.data;
      setOptions((prev) => {
        const exists = prev.some((option) => option._id === created._id);
        return exists ? prev : [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
      });
      onChange(created);
      showSnackbar('Location created successfully', 'success');
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to create location', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Autocomplete<ZoneOption, false, false, false>
      value={selectedValue}
      options={options}
      loading={loading || creating}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.inputValue || option.name;
      }}
      isOptionEqualToValue={(option, selected) => option._id === selected._id}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const inputValue = params.inputValue.trim();
        const exists = opts.some(
          (option) => option.name.toLowerCase() === inputValue.toLowerCase(),
        );

        if (inputValue !== '' && !exists) {
          filtered.push({
            _id: `create-${inputValue}`,
            name: inputValue,
            inputValue,
            isCreateOption: true,
          });
        }

        return filtered;
      }}
      onChange={(_, nextValue) => {
        if (!nextValue) {
          onChange(null);
          return;
        }

        if (nextValue.isCreateOption && nextValue.inputValue) {
          handleCreateZone(nextValue.inputValue);
          return;
        }

        onChange(nextValue);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {option.isCreateOption ? (
            <Button
              startIcon={<AddIcon />}
              size="small"
              sx={{ textTransform: 'none', px: 0 }}
            >
              Create "{option.inputValue}"
            </Button>
          ) : (
            <Typography>{option.name}</Typography>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || creating ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AdminZoneAutocomplete;
